# Flowstep Affiliate Program Terms

Last updated: 5 May 2026

Program owner:

**FlowstepDesign OÜ**<br>
Registry code: 16706771<br>
Address: Harju maakond, Tallinn, Kesklinna linnaosa, Liivalaia tn 36, 10132, Estonia<br>

(“**Flowstep**”, “**we**”, “**us**”)

These Affiliate Program Terms (“**Terms**”) govern participation in the Flowstep Affiliate Program (“**Program**”).

---

## 1. Acceptance

By enrolling in the Program, you (“**Affiliate**”, “**you**”) agree to these Terms. If you enrol on behalf of an entity, you represent that you have authority to bind that entity.

These Terms supplement Flowstep’s general Terms of Service and Privacy Policy. In the event of conflict, these Terms govern the Program.

---

## 2. Program platform

Flowstep uses a third-party service provider (“**Dub**”) to provide referral tracking, attribution, dashboards, and payout tooling. Flowstep is solely responsible for Program rules, eligibility, commissions, approvals, and enforcement. Dub is not a party to these Terms.

---

## 3. Eligibility and enrolment

- Participation requires an active **Flowstep account**.
- The **free plan is eligible**.
- **External (non-user) affiliates are not eligible to participate at this time.**
- Enrolment is **self-serve** and does not require manual approval.
- Flowstep reserves the right to suspend or terminate participation at any time in accordance with these Terms.

---

## 4. Referral links and promotion

Upon enrolment, you will receive a unique referral link (“**Referral Link**”).

### Permitted promotion

You may promote Flowstep using your Referral Link in a lawful, honest, and transparent manner, including educational content, reviews, and recommendations.

### Prohibited conduct

You may **not**:

- Make misleading or false claims about Flowstep.
- Impersonate Flowstep or imply employment, partnership, or endorsement.
- Engage in spam, automated traffic, cookie stuffing, click injection, or similar manipulation.
- Incentivize clicks or signups primarily to generate commissions.
- Refer yourself or earn commissions on your own purchases.
- Use Flowstep trademarks in domain names, social handles, or paid advertising **without written approval**.
- Bid on Flowstep-branded keywords (including misspellings) in paid advertising **unless explicitly approved in writing**.

You are responsible for complying with all applicable advertising, consumer protection, and disclosure laws, including clearly disclosing that you may earn a commission.

---

## 5. Brand and intellectual property

Flowstep grants you a limited, revocable, non-exclusive license to use approved Flowstep brand assets solely to promote Flowstep under the Program. This license may be revoked at any time.

---

## 6. Attribution rules

- **Attribution model:** last-touch referral.
- **Attribution window:** a referral is eligible if the referred user creates a Flowstep account within **30 days** of clicking the Referral Link.
- **Locking moment:** attribution is locked at account creation and does not change thereafter.
- **Uniqueness:** leads are unique by email address and may only be attributed once globally.
- **Channel precedence:** if a user is attributed to a paid acquisition channel at signup, the referral is deemed ineligible and no referral lead is recorded.

---

## 7. Qualified purchases and commissions

A “**Qualified Purchase**” is a paid Flowstep subscription purchase that:

- Is properly attributed under Section 6;
- Is not refunded, reversed, or charged back;
- Is not fraudulent or in violation of Flowstep’s terms.

### Commission structure

- **Rate:** **20%** of subscription fees actually paid.
- **Duration:** **3 months** from the customer’s first paid subscription start date.
- **Plans:** applies to monthly and annual subscriptions. Annual plans are commissioned on the full upfront amount, capped to 3 months.

“Subscription fees” exclude VAT/sales taxes, refunds, and payment processing charges.

**Effective date and existing referrals:** Updated commission rates apply to referrals logged on or after 5 May 2026. Referrals logged before this date remain on their original commission terms (40% for 12 months) and are not affected by this update.

### Customer incentive

Flowstep may offer referred customers a discount (currently **$5 off their first purchase**).

---

## 8. Commission status and payouts

- **Holding period:** **30 days** from the date the commission is created.
- **Payout frequency:** monthly.
- **Minimum payout:** €0.
- **Fees:** Flowstep covers standard payout processing fees at launch and may change this prospectively with notice.

---

## 9. Refunds, chargebacks, and clawbacks

- Refunds or chargebacks occurring during the holding period void the associated commission.
- Refunds or chargebacks after payout may be deducted from future commissions or reclaimed, to the extent permitted by law.

---

## 10. Taxes and verification

Affiliates are responsible for all taxes arising from commissions received. Flowstep may request identity or tax information if required by law or payment providers. Failure to provide required information may delay payouts.

---

## 11. Privacy

Personal data may be processed to operate the Program. Affiliates must comply with applicable data protection laws in their promotional activities.

---

## 12. Suspension and termination

Flowstep may suspend or terminate participation at any time for violation of these Terms, fraud, abuse, or legal or reputational risk. Unpaid commissions associated with violations may be forfeited.

---

## 13. Changes to the Program

Flowstep may modify these Terms or the Program prospectively. **Changes will not apply retroactively to commissions already earned under these Terms.** Continued participation constitutes acceptance of updated terms.

---

## 14. Relationship of parties

Affiliates are independent contractors. Nothing in these Terms creates an employment, agency, or partnership relationship.

---

## 15. Limitation of liability

To the maximum extent permitted by law, Flowstep’s total liability relating to the Program will not exceed the commissions paid to the Affiliate in the **three (3) months** preceding the claim.

---

## 16. Governing law

These Terms are governed by the laws of **Estonia**. Disputes are subject to the courts of **Harju County.**

---

## 17. Severability

If any provision of these Terms is held to be invalid or unenforceable, the remaining provisions will remain in full force and effect.

---

## 18. Contact

Legal notices and program support: [hello@flowstep.ai](mailto:hello@flowstep.ai)
